/**
 * Persisted pin/unpin for a chat thread (APP-3367). Replaces the old client-cache-only toggle, which a
 * window-refocus/remount refetch silently clobbered. PATCHes `{pinned}` through the shared api pipeline,
 * writes the shared threads cache (same key the ai-teammate drawer reads) optimistically, rolls back +
 * notifies on error, and invalidates on settle so server truth survives the next list refetch.
 */

import { DefaultError, useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { useNotifications } from "hooks/notifications"
import { Creators } from "model/apiWrapper"
import type { FetchThreadsResponse } from "connected-components/ai-teammate/types/thread"
import { getThreadsQueryKey, updateThreadInCache } from "connected-components/ai-teammate/utils/threads-cache"

interface PinThreadPayload {
  accountAlias: string
  threadId: string
  pinned: boolean
}

interface PinThreadContext {
  /** Only this thread's prior pin state — a full-list snapshot would clobber a concurrent mutation on rollback. */
  previousPinned: boolean | undefined
}

export function usePinThread() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()

  const { mutateAsync, isPending } = useMutation<unknown, DefaultError, PinThreadPayload, PinThreadContext>({
    // Only `pinned` rides the body — the backend patch is partial/no-clobber, so a title stays put.
    mutationFn: ({ accountAlias, threadId, pinned }) =>
      new Promise((resolve, reject) => {
        dispatch(Creators.patch(`/api/${accountAlias}/ai/chat/threads/${threadId}`, { pinned }, resolve, reject))
      }),
    onMutate: async ({ accountAlias, threadId, pinned }) => {
      const key = getThreadsQueryKey(accountAlias)
      // Stop an in-flight list GET from overwriting the optimistic write, then snapshot just this thread's pin.
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<FetchThreadsResponse>(key)
      const previousPinned = previous?.threads?.find((thread) => thread.threadId === threadId)?.pinned
      updateThreadInCache(queryClient, key, threadId, { pinned })
      return { previousPinned }
    },
    onError: (_error, { accountAlias, threadId, pinned }, context) => {
      // Restore only the mutated thread — a full-snapshot restore would rewind a sibling's concurrent pin/title write.
      if (context?.previousPinned !== undefined) {
        updateThreadInCache(queryClient, getThreadsQueryKey(accountAlias), threadId, { pinned: context.previousPinned })
      }
      notifs.error(
        pinned
          ? "An unexpected error occurred while pinning the conversation."
          : "An unexpected error occurred while unpinning the conversation.",
      )
    },
    // Server truth wins: the refetch is what makes the pin survive a later window-refocus/remount refetch.
    onSettled: (_data, _error, { accountAlias }) => {
      queryClient.invalidateQueries({ queryKey: getThreadsQueryKey(accountAlias) })
    },
  })

  return useMemo(() => ({
    pinThread: mutateAsync,
    isPinningThread: isPending,
  }), [mutateAsync, isPending])
}
